export const ORG = 'PhysicsXLtd';
export const PAGE_SIZE = 100;
export const PROXY_BASE = '/api/proxy';

export const githubGraphQL = async <T>(
  fetchApi: { fetch: typeof fetch },
  query: string,
  variables: Record<string, unknown>,
): Promise<T> => {
  const response = await fetchApi.fetch(`${PROXY_BASE}/github/api/graphql`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`GitHub GraphQL error: ${response.status}`);
  const json = await response.json();
  if (json.errors?.length) throw new Error(json.errors[0].message);
  return json.data as T;
};

export const resolveOwnerRepo = (repo: string): string =>
  repo.includes('/') ? repo : `${ORG}/${repo}`;

type OrgReposData = {
  organization: {
    repositories: {
      nodes: Array<{ name: string; nameWithOwner: string }>;
      pageInfo: { hasNextPage: boolean; endCursor: string };
    };
  };
};

type CodeSearchResponse = {
  items: Array<{ repository: { name: string; full_name: string } }>;
};

const fetchReposWithWorkflow = async (
  fetchApi: { fetch: typeof fetch },
  workflowFile: string,
): Promise<Array<{ name: string; full_name: string }>> => {
  const q = `filename:${workflowFile} path:.github/workflows org:${ORG} in:path`;
  const seen: Record<string, true> = {};
  const all: Array<{ name: string; full_name: string }> = [];
  let page = 1;

  while (true) {
    const params = new URLSearchParams({ q, per_page: String(PAGE_SIZE), page: String(page) });
    const response = await fetchApi.fetch(`${PROXY_BASE}/github/api/search/code?${params}`);
    if (!response.ok) throw new Error(`GitHub code search error: ${response.status}`);
    const data: CodeSearchResponse = await response.json();

    for (const item of data.items) {
      if (!seen[item.repository.full_name]) {
        seen[item.repository.full_name] = true;
        all.push({ name: item.repository.name, full_name: item.repository.full_name });
      }
    }

    if (data.items.length < PAGE_SIZE) break;
    page++;
  }

  return all.sort((a, b) => a.name.localeCompare(b.name));
};

type RepoBranchesData = {
  repository: {
    refs: {
      nodes: Array<{ name: string }>;
      pageInfo: { hasNextPage: boolean; endCursor: string };
    };
  };
};

export const fetchOrgRepos = async (
  fetchApi: { fetch: typeof fetch },
  workflowFile?: string,
): Promise<Array<{ name: string; full_name: string }>> => {
  if (workflowFile) {
    return fetchReposWithWorkflow(fetchApi, workflowFile);
  }

  const query = `
    query OrgRepos($cursor: String) {
      organization(login: "${ORG}") {
        repositories(first: ${PAGE_SIZE}, after: $cursor) {
          nodes {
            name
            nameWithOwner
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  `;

  const all: Array<{ name: string; full_name: string }> = [];
  let cursor: string | null = null;

  while (true) {
    const data = await githubGraphQL<OrgReposData>(fetchApi, query, { cursor });
    const { nodes, pageInfo } = data.organization.repositories;
    for (const n of nodes) {
      all.push({ name: n.name, full_name: n.nameWithOwner });
    }
    if (!pageInfo.hasNextPage) break;
    cursor = pageInfo.endCursor;
  }

  return all.sort((a, b) => a.name.localeCompare(b.name));
};

export const fetchRepoBranches = async (
  fetchApi: { fetch: typeof fetch },
  owner: string,
  repo: string,
): Promise<Array<{ name: string }>> => {
  const query = `
    query RepoBranches($owner: String!, $repo: String!, $cursor: String) {
      repository(owner: $owner, name: $repo) {
        refs(refPrefix: "refs/heads/", first: ${PAGE_SIZE}, after: $cursor) {
          nodes { name }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  `;

  const all: Array<{ name: string }> = [];
  let cursor: string | null = null;

  while (true) {
    const data = await githubGraphQL<RepoBranchesData>(fetchApi, query, { owner, repo, cursor });
    const { nodes, pageInfo } = data.repository.refs;
    all.push(...nodes);
    if (!pageInfo.hasNextPage) break;
    cursor = pageInfo.endCursor;
  }

  return all.sort((a, b) => a.name.localeCompare(b.name));
};
